import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import FocusTrap from 'focus-trap-react';
import styled, { keyframes } from 'styled-components';
import { useCookieConsent } from '../context/CookieConsentContext';
import { useLocalized } from '../hooks/useLocalized';

// --- Banner animations ---

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

// --- Banner styled components ---

const BannerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 10000;

  @media (max-width: 768px) {
    background: transparent;
  }
`;

const BannerWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10001;
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  animation: ${slideUp} 0.5s cubic-bezier(0.23, 1, 0.32, 1) forwards;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const BannerCard = styled.div`
  background: #ffffff;
  max-width: 720px;
  width: 100%;
  padding: 1.5rem 2rem;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 1.25rem 1.25rem 1.5rem;
    border: none;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
  }
`;

const BannerText = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.6;
  color: #555;
  text-align: center;
  margin: 0 0 1rem 0;

  @media (max-width: 768px) {
    text-align: left;
    font-size: 0.75rem;
    color: #555;
    margin: 0 0 1rem 0;
  }
`;

const PolicyLink = styled(Link)`
  display: block;
  margin-top: 0.75rem;
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.65rem;
  color: #999;
  text-align: center;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const BannerActions = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const BannerButton = styled.button`
  flex: 1;
  padding: 0.6rem 1.25rem;
  background: transparent;
  color: #1a1a1a;
  border: 1px solid rgba(0, 0, 0, 0.15);
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.3s ease, background 0.3s ease, color 0.3s ease;

  &:hover {
    border-color: #1a1a1a;
    background: #1a1a1a;
    color: #ffffff;
  }

  @media (max-width: 768px) {
    padding: 0.65rem 1.25rem;
    font-size: 0.65rem;
    border: 1px solid rgba(0, 0, 0, 0.15);
  }
`;

// --- Settings panel styled components ---

const SettingsOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99999;
  opacity: ${props => (props.$isOpen ? '1' : '0')};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const SettingsPanel = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  background: #ffffff;
  z-index: 100000;
  transform: translateX(${props => (props.$isOpen ? '0' : '100%')});
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1), visibility 0.4s ease;
  box-shadow: -2px 0 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SettingsHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SettingsTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.125rem;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  opacity: 0.6;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: #1a1a1a;
    stroke-width: 1;
  }
`;

const SettingsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem;
`;

const CategoryBlock = styled.div`
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const CategoryName = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  letter-spacing: 0.02em;
`;

const CategoryDescription = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.75rem;
  font-weight: 400;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

// --- Toggle switch ---

const ToggleTrack = styled.button<{ $checked: boolean; $disabled?: boolean }>`
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  background: ${props => (props.$checked ? '#1a1a1a' : '#e0e0e0')};
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  transition: background 0.3s ease;
  flex-shrink: 0;
  padding: 0;
`;

const ToggleThumb = styled.span<{ $checked: boolean }>`
  position: absolute;
  top: 3px;
  left: ${props => (props.$checked ? '21px' : '3px')};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  transition: left 0.3s ease;
`;

// --- Settings footer ---

const SettingsFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 0.9rem 2rem;
  background: #1a1a1a;
  color: #ffffff;
  border: none;
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.85;
  }
`;

// --- Component ---

const CookieConsent: React.FC = () => {
  const {
    consent,
    hasDecided,
    acceptAll,
    rejectAll,
    savePreferences,
    isSettingsOpen,
    setIsSettingsOpen,
  } = useCookieConsent();
  const { getLocalized } = useLocalized();

  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);
  const [bannerVisible, setBannerVisible] = useState(false);

  // Sync toggle states when settings panel opens (for returning users)
  useEffect(() => {
    if (isSettingsOpen && consent) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [isSettingsOpen, consent]);

  // Show banner with a slight delay for a polished feel
  useEffect(() => {
    if (!hasDecided) {
      const timer = setTimeout(() => setBannerVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, [hasDecided]);

  // Close settings panel — re-show banner if user hasn't decided yet
  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
    if (!hasDecided) {
      setBannerVisible(true);
    }
  }, [setIsSettingsOpen, hasDecided]);

  // Close settings panel on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSettingsOpen) {
        handleCloseSettings();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSettingsOpen, handleCloseSettings]);

  const handleManagePreferences = useCallback(() => {
    setBannerVisible(false);
    setIsSettingsOpen(true);
  }, [setIsSettingsOpen]);

  const handleSavePreferences = useCallback(() => {
    savePreferences({ analytics, marketing });
  }, [analytics, marketing, savePreferences]);

  const handleAcceptAll = useCallback(() => {
    acceptAll();
    setBannerVisible(false);
  }, [acceptAll]);

  const handleRejectAll = useCallback(() => {
    rejectAll();
    setBannerVisible(false);
  }, [rejectAll]);

  // Don't render anything if user already decided and settings panel is closed
  if (hasDecided && !isSettingsOpen) return null;

  return (
    <>
      {/* Cookie banner */}
      {!hasDecided && bannerVisible && (
        <>
          <BannerOverlay />
          <BannerWrapper>
            <BannerCard>
              <BannerText>
                {getLocalized(
                  'Bu site, deneyiminizi iyileştirmek için çerez kullanmaktadır.',
                  'This site uses cookies to improve your experience.'
                )}
              </BannerText>
              <BannerActions>
                <BannerButton onClick={handleRejectAll}>
                  {getLocalized('Tümünü Reddet', 'Reject All')}
                </BannerButton>
                <BannerButton onClick={handleManagePreferences}>
                  {getLocalized('Tercihlerimi Yönet', 'Manage Preferences')}
                </BannerButton>
                <BannerButton onClick={handleAcceptAll}>
                  {getLocalized('Tümünü Kabul Et', 'Accept All')}
                </BannerButton>
              </BannerActions>
              <PolicyLink to="/privacy-policy">
                {getLocalized(
                  'Çerez Politikamızı inceleyebilirsiniz.',
                  'View our Cookie Policy.'
                )}
              </PolicyLink>
            </BannerCard>
          </BannerWrapper>
        </>
      )}

      {/* Settings panel */}
      <SettingsOverlay $isOpen={isSettingsOpen} onClick={handleCloseSettings} />
      <FocusTrap active={isSettingsOpen} focusTrapOptions={{ allowOutsideClick: true, escapeDeactivates: false, checkCanFocusTrap: () => new Promise<void>(resolve => setTimeout(resolve, 450)) }}>
      <SettingsPanel $isOpen={isSettingsOpen} role="dialog" aria-modal="true" aria-label="Cookie settings">
        <SettingsHeader>
          <SettingsTitle>
            {getLocalized('\u00c7erez Ayarlar\u0131', 'Cookie Settings')}
          </SettingsTitle>
          <CloseButton onClick={handleCloseSettings} aria-label="Close cookie settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </SettingsHeader>

        <SettingsContent>
          {/* Necessary cookies - always on */}
          <CategoryBlock>
            <CategoryHeader>
              <CategoryName>
                {getLocalized('Zorunlu \u00c7erezler', 'Necessary Cookies')}
              </CategoryName>
              <ToggleTrack $checked $disabled aria-label="Necessary cookies - always enabled">
                <ToggleThumb $checked />
              </ToggleTrack>
            </CategoryHeader>
            <CategoryDescription>
              {getLocalized(
                'Üyelik işlemleri, alışveriş sepeti ve dil tercihi gibi temel site işlevleri için gereklidir. Bu çerezler devre dışı bırakılamaz.',
                'Required for essential site functions like authentication, shopping cart, and language preferences. These cookies cannot be disabled.'
              )}
            </CategoryDescription>
          </CategoryBlock>

          {/* Analytics cookies */}
          <CategoryBlock>
            <CategoryHeader>
              <CategoryName>
                {getLocalized('Analitik \u00c7erezler', 'Analytics Cookies')}
              </CategoryName>
              <ToggleTrack
                $checked={analytics}
                onClick={() => setAnalytics(prev => !prev)}
                aria-label={getLocalized('Analitik \u00e7erezleri a\u00e7/kapat', 'Toggle analytics cookies')}
              >
                <ToggleThumb $checked={analytics} />
              </ToggleTrack>
            </CategoryHeader>
            <CategoryDescription>
              {getLocalized(
                'Sitemizin nasıl kullanıldığını anlamamıza ve deneyiminizi iyileştirmemize yardımcı olur.',
                'Helps us understand how our site is used and improve your experience.'
              )}
            </CategoryDescription>
          </CategoryBlock>

          {/* Marketing cookies */}
          <CategoryBlock>
            <CategoryHeader>
              <CategoryName>
                {getLocalized('Pazarlama \u00c7erezleri', 'Marketing Cookies')}
              </CategoryName>
              <ToggleTrack
                $checked={marketing}
                onClick={() => setMarketing(prev => !prev)}
                aria-label={getLocalized('Pazarlama \u00e7erezlerini a\u00e7/kapat', 'Toggle marketing cookies')}
              >
                <ToggleThumb $checked={marketing} />
              </ToggleTrack>
            </CategoryHeader>
            <CategoryDescription>
              {getLocalized(
                'Size daha alakalı reklamlar ve içerikler sunmamızı sağlar.',
                'Allows us to show you more relevant ads and content.'
              )}
            </CategoryDescription>
          </CategoryBlock>
        </SettingsContent>

        <SettingsFooter>
          <SaveButton onClick={handleSavePreferences}>
            {getLocalized('Se\u00e7imi Kaydet', 'Save Preferences')}
          </SaveButton>
        </SettingsFooter>
      </SettingsPanel>
      </FocusTrap>
    </>
  );
};

export default CookieConsent;
